import type { ReactNode } from "react";
import { toast } from "sonner";

import { ToastrBody } from "./ToastrBody";
import {
  activeToasts,
  getContentDir,
  parseConfig,
  resolveErrorMessage,
  resolveMessage,
  toastKey,
} from "./utils";

export interface ToastrConfig {
  /** Label for an action button inside the toast. */
  buttonLabel?: string;
  /** Callback when the action button is clicked. */
  onClick?: () => void;
  /** Whether to show the type icon in the toast. */
  showIcon?: boolean;
  /** Whether the toast can be dismissed (shows X button + enables swipe). Defaults to true. */
  dismissible?: boolean;
  /** Whether to show the copy-to-clipboard affordance in error toasts. Defaults to true. */
  showCopyButton?: boolean;
  /** Additional sonner toast options (duration, position, etc.). */
  [key: string]: unknown;
}

export type ToastrMessage =
  | ReactNode
  | { noticeCode?: string; customMessage?: string; notice?: string };

export type ToastrErrorInput =
  | string
  | Error
  | {
      response?: { data?: Record<string, unknown> };
      message?: string;
      isAxiosError?: boolean;
    }
  | ToastrMessage;

type SonnerType = "success" | "info" | "warning" | "error";

function showToast(
  type: SonnerType,
  message: ReactNode,
  config?: ToastrConfig
): string | number | undefined {
  const {
    buttonLabel,
    onClick,
    showIcon,
    dismissible = true,
    showCopyButton = true,
    onDismiss: userOnDismiss,
    onAutoClose: userOnAutoClose,
    ...restConfig
  } = config ?? {};

  const key = toastKey(type, message, buttonLabel);

  if (key !== null && activeToasts.has(key)) return undefined;
  if (key !== null) activeToasts.add(key);

  // Error toasts stay until dismissed so the message can be read and copied.
  const options: Record<string, unknown> = {
    ...(type === "error" && { duration: Infinity }),
    ...restConfig,
    position: getContentDir() === "rtl" ? "bottom-right" : "bottom-left",
    dismissible,
    onDismiss: (t: unknown) => {
      if (key !== null) activeToasts.delete(key);
      (userOnDismiss as ((t: unknown) => void) | undefined)?.(t);
    },
    onAutoClose: (t: unknown) => {
      if (key !== null) activeToasts.delete(key);
      (userOnAutoClose as ((t: unknown) => void) | undefined)?.(t);
    },
  };

  const copyableMessage =
    showCopyButton && type === "error" && typeof message === "string"
      ? message
      : undefined;

  return toast.custom(
    id => (
      <ToastrBody
        buttonLabel={buttonLabel}
        copyableMessage={copyableMessage}
        dismissible={dismissible}
        message={message}
        showIcon={showIcon}
        type={type}
        onAction={() => {
          try {
            onClick?.();
          } finally {
            toast.dismiss(id);
          }
        }}
        onDismiss={() => toast.dismiss(id)}
      />
    ),
    options
  );
}

const Toastr = {
  success(message: ToastrMessage, ...args: unknown[]) {
    return showToast("success", resolveMessage(message), parseConfig(args));
  },

  info(message: ToastrMessage, ...args: unknown[]) {
    return showToast("info", resolveMessage(message), parseConfig(args));
  },

  show(message: ToastrMessage, ...args: unknown[]) {
    return showToast("info", resolveMessage(message), parseConfig(args));
  },

  warning(message: ToastrMessage, ...args: unknown[]) {
    return showToast("warning", resolveMessage(message), parseConfig(args));
  },

  error(errorObject: ToastrErrorInput, ...args: unknown[]) {
    const msg = resolveErrorMessage(errorObject);
    return showToast("error", msg, parseConfig(args));
  },
};

export { Toastr };
